import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { ContextSession } from "../../services/ContextSession";
import { useNavigate } from "react-router-dom";

const API_BASE =
  "https://bei360.injupemp.gob.hn:8444/api/SGOPER/constancia-configuracion/";
const API_CREATE =
  "https://bei360.injupemp.gob.hn:8444/api/SGOPER/constancia-configuracion/create/";
const API_DETAIL_BASE =
  "https://bei360.injupemp.gob.hn:8444/api/SGOPER/constancia-configuracion/id/";

const GENEROS = [
  { value: "M", label: "Masculino" },
  { value: "F", label: "Femenino" },
  { value: "O", label: "Otro" },
];
const MAIN_BLUE = "#19498c";

function getDataUrl(mimeType, logoBase64) {
  if (!logoBase64) return null;
  if (logoBase64.startsWith("data:")) return logoBase64;
  const mime = mimeType || "image/png";
  return `data:${mime};base64,${logoBase64}`;
}

export default function ConfigConstanciasHistorial() {
  const navigate = useNavigate();
  const sessi = useContext(ContextSession);
  const token = sessi?.sessionData?.token || "";

  const [rows, setRows] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const [viewOpen, setViewOpen] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detail, setDetail] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [tokenExpiredOpen, setTokenExpiredOpen] = useState(false);

  const [form, setForm] = useState({
    encargado_nombre: "",
    encargado_cargo: "",
    encargado_genero: "",
    logo_file: null,
  });
  const [saving, setSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState("");

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
    }),
    [token],
  );

  const isTokenInvalidResponse = (result) => {
    if (!result || typeof result !== "object") return false;
    if (result.code === "token_not_valid") return true;
    if (typeof result.detail === "string" && result.detail.toLowerCase().includes("token")) {
      return true;
    }
    return false;
  };

  const handleTokenExpired = () => {
    setTokenExpiredOpen(true);
  };

  const handleTokenExpiredConfirm = () => {
    setTokenExpiredOpen(false);
    sessi.clearSession();
    navigate("/");
  };

  useEffect(() => {
    if (!form.logo_file) {
      setLogoPreview("");
      return;
    }
    const preview = URL.createObjectURL(form.logo_file);
    setLogoPreview(preview);
    return () => URL.revokeObjectURL(preview);
  }, [form.logo_file]);

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatOnlyDate = (value) => {
    if (!value) return "N/A";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) {
      const text = String(value);
      return text.includes("T") ? text.split("T")[0] : text;
    }
    return d.toLocaleDateString("es-HN");
  };

  const loadList = async () => {
    setLoadingList(true);
    setError("");
    setMessage("");
    try {
      const response = await fetch(API_BASE, {
        method: "GET",
        headers: authHeaders,
      });
      const data = await response.json().catch(() => null);
      if (response.status === 401 && isTokenInvalidResponse(data)) {
        handleTokenExpired();
        return;
      }
      if (!response.ok) {
        throw new Error(`No se pudo cargar la lista (${response.status}).`);
      }
      setRows(Array.isArray(data) ? data : []);
      setMessage("Lista cargada correctamente.");
    } catch (err) {
      setError(err.message || "Error al cargar lista.");
    } finally {
      setLoadingList(false);
    }
  };

  const loadDetail = async (id) => {
    setViewOpen(true);
    setLoadingDetail(true);
    setDetail(null);
    setError("");
    try {
      const candidates = [
        `${API_DETAIL_BASE}${id}/`,
        `${API_DETAIL_BASE}?id=${id}`,
        `${API_BASE}${id}/`,
      ];

      let okResponse = null;
      let detailData = null;
      for (const url of candidates) {
        const response = await fetch(url, {
          method: "GET",
          headers: authHeaders,
        });
        const result = await response.json().catch(() => null);
        if (response.status === 401 && isTokenInvalidResponse(result)) {
          handleTokenExpired();
          return;
        }
        if (response.ok) {
          okResponse = response;
          detailData = result;
          break;
        }
      }

      if (!okResponse) {
        throw new Error("No se encontró el detalle para este registro.");
      }

      setDetail(detailData);
    } catch (err) {
      setError(err.message || "Error al cargar detalle.");
    } finally {
      setLoadingDetail(false);
    }
  };

  const onInputChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const onSelectLogo = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setForm((prev) => ({
      ...prev,
      logo_file: file,
    }));
  };

  const clearForm = () => {
    setForm({
      encargado_nombre: "",
      encargado_cargo: "",
      encargado_genero: "",
      logo_file: null,
    });
  };

  const saveNew = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      if (
        !form.encargado_nombre.trim() ||
        !form.encargado_genero ||
        !form.logo_file
      ) {
        throw new Error("Los campos obligatorios son: Nombre, Genero y Logo.");
      }

      const payload = new FormData();
      payload.append("encargado_nombre", form.encargado_nombre.trim());
      payload.append("encargado_genero", form.encargado_genero);
      if (form.encargado_cargo.trim()) {
        payload.append("encargado_cargo", form.encargado_cargo.trim());
      }
      payload.append("logo_file", form.logo_file);

      const response = await fetch(API_CREATE, {
        method: "POST",
        headers: {
          ...authHeaders,
        },
        body: payload,
      });

      const result = await response.json().catch(() => ({}));
      if (response.status === 401 && isTokenInvalidResponse(result)) {
        handleTokenExpired();
        return;
      }
      if (!response.ok) {
        throw new Error(
          typeof result === "object"
            ? JSON.stringify(result)
            : "No se pudo registrar.",
        );
      }

      setMessage("Configuracion registrada correctamente.");
      clearForm();
      setCreateOpen(false);
      await loadList();
    } catch (err) {
      setError(err.message || "Error al registrar configuracion.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Configuracion de Constancias de Historial
      </Typography>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }}>
        <Button
          variant="contained"
          color="success"
          startIcon={<AddCircleOutlineIcon />}
          onClick={() => setCreateOpen(true)}
        >
          Nueva Configuracion
        </Button>
      </Stack>

      {message ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          {message}
        </Alert>
      ) : null}
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Listado de Configuraciones
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Encargado</TableCell>
                      <TableCell>Cargo</TableCell>
                      <TableCell>Genero</TableCell>
                      <TableCell>Activo</TableCell>
                      <TableCell>Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          No hay datos cargados.
                        </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>{row.id}</TableCell>
                          <TableCell>{row.encargado_nombre}</TableCell>
                          <TableCell>{row.encargado_cargo}</TableCell>
                          <TableCell>{row.encargado_genero}</TableCell>
                          <TableCell>{row.activo ? "Si" : "No"}</TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<VisibilityIcon />}
                              sx={{ color: MAIN_BLUE, borderColor: MAIN_BLUE }}
                              onClick={() => loadDetail(row.id)}
                            >
                              Ver
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Detalle Configuracion</DialogTitle>
        <DialogContent>
          {loadingDetail ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress />
            </Box>
          ) : detail ? (
            <Stack spacing={1} sx={{ mt: 1 }}>
              <Typography>
                <b>Encargado:</b> {detail.encargado_nombre}
              </Typography>
              <Typography>
                <b>Cargo:</b> {detail.encargado_cargo}
              </Typography>
              <Typography>
                <b>Activo:</b> {detail.activo ? "Si" : "No"}
              </Typography>
              <Typography>
                <b>Fecha Registro:</b> {formatOnlyDate(detail.fecha_registro)}
              </Typography>
              {detail.logo_base64 ? (
                <Box
                  component="img"
                  src={getDataUrl(detail.logo_mime_type, detail.logo_base64)}
                  alt="Logo configuracion"
                  sx={{
                    maxHeight: 120,
                    maxWidth: 260,
                    border: "1px solid #ddd",
                    p: 1,
                    mt: 1,
                  }}
                />
              ) : (
                <Typography>
                  <b>Logo:</b> Sin logo
                </Typography>
              )}
            </Stack>
          ) : (
            <Typography>No hay detalle disponible.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOpen(false)} sx={{ color: MAIN_BLUE }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={createOpen}
        onClose={() => (!saving ? setCreateOpen(false) : null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Registrar Nueva Configuracion</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Campos obligatorios: Nombre, Genero y Logo.
            </Typography>
            <TextField
              label="Encargado Nombre"
              value={form.encargado_nombre}
              onChange={(e) => onInputChange("encargado_nombre", e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Encargado Cargo"
              value={form.encargado_cargo}
              onChange={(e) => onInputChange("encargado_cargo", e.target.value)}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="genero-create-label">Genero</InputLabel>
              <Select
                labelId="genero-create-label"
                label="Genero"
                value={form.encargado_genero}
                onChange={(e) => onInputChange("encargado_genero", e.target.value)}
              >
                {GENEROS.map((op) => (
                  <MenuItem key={op.value} value={op.value}>
                    {op.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              component="label"
              variant="outlined"
              sx={{ color: MAIN_BLUE, borderColor: MAIN_BLUE }}
            >
              Seleccionar Logo *
              <input type="file" hidden accept="image/*" onChange={onSelectLogo} />
            </Button>

            {form.logo_file ? (
              <Typography variant="body2">Logo: {form.logo_file.name}</Typography>
            ) : null}
            {form.logo_file ? (
              <Box
                component="img"
                src={logoPreview}
                alt="Vista previa logo"
                sx={{ maxHeight: 90, maxWidth: 220, border: "1px solid #ddd", p: 1 }}
              />
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCreateOpen(false)}
            disabled={saving}
            sx={{ color: MAIN_BLUE }}
          >
            Cancelar
          </Button>
          <Button
            variant="outlined"
            onClick={clearForm}
            disabled={saving}
            sx={{ color: MAIN_BLUE, borderColor: MAIN_BLUE }}
          >
            Limpiar
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={18} /> : <AddCircleOutlineIcon />}
            onClick={saveNew}
            disabled={saving}
            sx={{ backgroundColor: MAIN_BLUE, "&:hover": { backgroundColor: MAIN_BLUE } }}
          >
            Registrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={tokenExpiredOpen} onClose={() => {}} maxWidth="xs" fullWidth>
        <DialogTitle>Sesion vencida</DialogTitle>
        <DialogContent>
          <Typography>El token vencio. Debe volver a ingresar al sistema.</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={handleTokenExpiredConfirm}
            sx={{ backgroundColor: MAIN_BLUE, "&:hover": { backgroundColor: MAIN_BLUE } }}
          >
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
